import { abTests, defaultStarterInvestorGoal, defaultStarterInvestorGoalName } from '@src/constants';
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { getGoalAmountOptions, getGoalIncomeAmountSelect, getGoalSuggestion } from '@api/investment-goals';
import { GoalIncomeSelectAmount, GoalSelectAmount, GoalSuggestion, SaveInvestorGoal } from 'types/investment-goals';
import { AutoInvestSchedule } from 'types/auto-invest';
import big from 'big.js';
import { getAbTestGroup } from '@src/api/ab-test';
import { getRecurringInvestmentSchedule } from '@api/auto-invest';
import { investmentEntity } from './investment-entity';
import store from '..';
import { updateDrip } from '@api/drip';

export interface InvestorGoalState {
	saveInvestorGoal: SaveInvestorGoal | null;
	minimumAmount: string;
	maxAmount: string;
	autoInvestSchedule: AutoInvestSchedule | null;
}

@Module({
	dynamic: true,
	namespaced: true,
	name: 'investmentGoals',
	store
})
class InvestorGoalModule extends VuexModule {
	saveInvestorGoal: SaveInvestorGoal = {
		goalDripEnabled: true,
		goalName: '',
		goalType: null,
		targetValue: '',
		targetIncome: '',
		years: ''
	};

	monthlyContributionAmount = '';
	minimumAmount = '0';
	maxAmount = '0';
	minimumMonthlyInvestment = '10';
	maximumMonthlyInvestment = '20000';
	autoInvestSchedule: AutoInvestSchedule | null = null;
	autoInvestSuggestedAmount = '';
	autoInvestScheduleUpdated = false;

	showPrefilledTemplateOption = false;
	showMonthlySuggestionAmounts = false;

	get isAutoInvestGreaterThanSuggestedAmount(): boolean {
		return parseInt(this.autoInvestSchedule?.monthlyAmount ?? '0') >= parseInt(this.autoInvestSuggestedAmount);
	}

	@Action({ rawError: true })
	public async getGoalAmountOptions(years: string): Promise<GoalSelectAmount> {
		const goalAmountOptions = await getGoalAmountOptions(years);
		this.UPDATE_MINIMUM_AMOUNT(goalAmountOptions.minimumTargetValue);
		this.UPDATE_MAX_AMOUNT(goalAmountOptions.maxTargetValue);

		if (goalAmountOptions.maxMonthlyAmount) {
			this.UPDATE_MAX_GROWTH_MONTHLY_AMOUNT(goalAmountOptions.maxMonthlyAmount);
		}

		return goalAmountOptions;
	}

	@Action({ rawError: true })
	public async getIncomeGoalAmount(years: string): Promise<GoalIncomeSelectAmount> {
		const goalIncomeSelectAmount = await getGoalIncomeAmountSelect(years);
		this.UPDATE_MINIMUM_AMOUNT(goalIncomeSelectAmount.minimumTargetIncome);
		this.UPDATE_MAX_AMOUNT(goalIncomeSelectAmount.maxTargetIncome);
		return goalIncomeSelectAmount;
	}

	@Action({ rawError: true })
	public async getGoalSuggestion(params: Partial<SaveInvestorGoal>): Promise<GoalSuggestion> {
		const goalSuggestion = await getGoalSuggestion(params);
		const minimum =
			goalSuggestion.goalCategory === 'GROWTH_GOAL'
				? goalSuggestion.minimumTargetValue
				: goalSuggestion.minimumTargetIncome;
		this.UPDATE_MINIMUM_AMOUNT(minimum ?? '0');
		return goalSuggestion;
	}

	@Action({ rawError: true })
	public async setShowPrefilledTemplateOption(): Promise<void> {
		const totalAccountValue = big(investmentEntity.positions?.totalAccountValue ?? '0');
		const maxAccountValueForTest = big(500);
		if (investmentEntity.currentServiceLevel.name === 'STARTER' && totalAccountValue.lt(maxAccountValueForTest)) {
			const testGroup = await getAbTestGroup('2021-11-17_starter-goals-template-v2');
			this.UPDATE_SHOW_PREFILLED_TEMPLATE_OPTION(testGroup === 'INCLUDE_PREFILLED_OPTION');
		}
	}

	@Action({ rawError: true })
	public async setShowMonthlySuggestionAmounts(): Promise<void> {
		const abTestGroup = (await getAbTestGroup(abTests.GROWTH_GOALS_MONTHLY_SUGGESTION.testName)) || '';
		this.UPDATE_SHOW_MONTHLY_SUGGESTION_AMOUNTS(abTestGroup === abTests.GROWTH_GOALS_MONTHLY_SUGGESTION.inGroup);
	}

	@Action({ rawError: true })
	public async submitGoalType(params: Partial<SaveInvestorGoal>): Promise<string> {
		if (this.showPrefilledTemplateOption && params.goalName === defaultStarterInvestorGoalName) {
			this.UPDATE_SAVE_INVESTOR_GOAL(defaultStarterInvestorGoal);
			this.getGoalAmountOptions(defaultStarterInvestorGoal.years);
			return 'investment-goal-confirm';
		}

		const updatedInvestorGoal = {
			...this.saveInvestorGoal,
			...params
		};
		this.UPDATE_SAVE_INVESTOR_GOAL(updatedInvestorGoal);
		return 'investment-goal-time-horizon';
	}

	@Action({ rawError: true })
	public async submitTimeHorizon(years: string): Promise<string> {
		const updatedInvestorGoal = {
			...this.saveInvestorGoal,
			years
		};
		this.UPDATE_SAVE_INVESTOR_GOAL(updatedInvestorGoal);
		return 'investment-goal-amount';
	}

	@Action({ rawError: true })
	public async submitTargetAmount(targetValue: string): Promise<string> {
		const updatedInvestorGoal = {
			...this.saveInvestorGoal,
			targetValue
		};
		this.UPDATE_SAVE_INVESTOR_GOAL(updatedInvestorGoal);
		return 'investment-goal-confirm';
	}

	@Action({ rawError: true })
	public async submitTargetIncome(targetIncome: string): Promise<string> {
		const updatedInvestorGoal = {
			...this.saveInvestorGoal,
			targetIncome
		};
		this.UPDATE_SAVE_INVESTOR_GOAL(updatedInvestorGoal);
		return 'investment-goal-confirm';
	}

	@Action({ rawError: true })
	public async submitMonthlyContributionAmount(monthlyContributionAmount: string): Promise<void> {
		this.UPDATE_MONTHLY_CONTRIBUTION_AMOUNT(monthlyContributionAmount);
	}

	@Action({ rawError: true })
	public async submitDrip(): Promise<void> {
		await updateDrip('RIA_REINVESTMENT');
	}

	@Action({ rawError: true })
	public async getAutoInvestSchedule(): Promise<void> {
		try {
			const schedule = await getRecurringInvestmentSchedule();
			this.UPDATE_AUTO_INVEST_SCHEDULE(schedule);
		} catch {}
	}

	@Action
	public updateDefaultGoalCreatedPreferences(monthlySuggestedAmount: string): void {
		this.UPDATE_SUGGESTED_AUTO_INVEST_AMOUNT(monthlySuggestedAmount);
		investmentEntity.UPDATE_INVESTOR_GOAL_EXISTS(true);
	}

	@Action
	public reset(): void {
		const defaultSaveInvestorGoal: SaveInvestorGoal = {
			goalDripEnabled: true,
			goalName: '',
			goalType: null,
			targetValue: '',
			targetIncome: '',
			years: ''
		};
		this.UPDATE_SAVE_INVESTOR_GOAL(defaultSaveInvestorGoal);
		this.UPDATE_AUTO_INVEST_SCHEDULE(null);
		this.UPDATE_AUTO_INVEST_SCHEDULE_UPDATED(false);

		this.UPDATE_MONTHLY_CONTRIBUTION_AMOUNT('');
		this.UPDATE_SHOW_MONTHLY_SUGGESTION_AMOUNTS(false);
	}

	@Mutation
	public UPDATE_SAVE_INVESTOR_GOAL(saveInvestorGoal: SaveInvestorGoal): void {
		this.saveInvestorGoal = saveInvestorGoal;
	}

	@Mutation
	public UPDATE_MAX_AMOUNT(maxAmount: string): void {
		this.maxAmount = maxAmount;
	}

	@Mutation
	public UPDATE_MAX_GROWTH_MONTHLY_AMOUNT(maxMonthlyAmount: string): void {
		this.maximumMonthlyInvestment = maxMonthlyAmount;
	}

	@Mutation
	public UPDATE_MINIMUM_AMOUNT(minimumAmount: string): void {
		this.minimumAmount = minimumAmount;
	}

	@Mutation
	public UPDATE_MONTHLY_CONTRIBUTION_AMOUNT(monthlyContributionAmount: string): void {
		this.monthlyContributionAmount = monthlyContributionAmount;
	}

	@Mutation
	public UPDATE_AUTO_INVEST_SCHEDULE(autoInvestSchedule: AutoInvestSchedule | null): void {
		this.autoInvestSchedule = autoInvestSchedule;
	}

	@Mutation
	public UPDATE_SUGGESTED_AUTO_INVEST_AMOUNT(autoInvestSuggestedAmount: string): void {
		this.autoInvestSuggestedAmount = autoInvestSuggestedAmount;
	}

	@Mutation
	public UPDATE_AUTO_INVEST_SCHEDULE_UPDATED(autoInvestScheduleUpdated: boolean): void {
		this.autoInvestScheduleUpdated = autoInvestScheduleUpdated;
	}

	@Mutation
	public UPDATE_SHOW_PREFILLED_TEMPLATE_OPTION(showPrefilledTemplateOption: boolean): void {
		this.showPrefilledTemplateOption = showPrefilledTemplateOption;
	}

	@Mutation
	public UPDATE_SHOW_MONTHLY_SUGGESTION_AMOUNTS(showMonthlySuggestionAmounts: boolean): void {
		this.showMonthlySuggestionAmounts = showMonthlySuggestionAmounts;
	}
}

export const investmentGoals = getModule(InvestorGoalModule);
